import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {WeightedNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {WeightedCardScratchpadState} from '../../scratchpad/weightedCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const editWeightedOutputInputSchema = z.object({
  outputId: z.number().describe('The id of the output to edit'),
  name: z.string().optional().nullable().describe('The new name for the output'),
  weight: z
    .number()
    .int({message: 'Weight must be a whole number'})
    .positive({message: 'Weight must be positive'})
    .min(1, {message: 'Weight must be at least 1'})
    .optional()
    .nullable()
    .describe('The new weight for the output (positive integer, minimum 1)'),
});

export default function editWeightedOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof editWeightedOutputInputSchema, WeightedCardScratchpadState>,
  ) {
    const {outputId, name, weight} = params.input;
    const validatedScratchpad = validateAgentScratchpad<WeightedCardScratchpadState>(
      params.toolUtils.agentScratchpad,
      NodeType.WEIGHTED,
    );
    const {card} = validatedScratchpad;

    const existingOutput = card.outputs.find((output) => output.id === outputId) as
      | WeightedNodeOutputData
      | undefined;

    if (!existingOutput) {
      return toolError(`Weighted output with id ${outputId} not found.`);
    }

    const changes: string[] = [];

    if (name !== undefined && name !== null) {
      existingOutput.name = name;
      changes.push(`name to "${name}"`);
    }

    if (weight !== undefined && weight !== null) {
      existingOutput.weight = weight;
      changes.push(`weight to ${weight}`);
    }

    if (changes.length === 0) {
      return toolError('No changes specified for weighted output');
    }

    return toolResult(`Weighted output updated: ${changes.join(', ')}`);
  }

  return tool<typeof editWeightedOutputInputSchema, WeightedCardScratchpadState>({
    name: V2CardAgentToolName.EditWeightedOutput,
    displayName: 'Editing weighted output',
    description: 'Edit an existing weighted output name and/or weight.',
    inputSchema: editWeightedOutputInputSchema,
    execute,
  });
}
